import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as userService from "@/features/users/userService";
import { useBankingValidation } from "@/features/users/useUsers";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@shared/components/ui/avatar";
import BankingStatus from "@features/banking/components/BankingStatus";
import BankingValidationDialog from "@features/banking/components/BankingValidationDialog";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  X,
  User,
  CreditCard,
  Clock,
  FileText,
  Building2,
  Shield,
} from "lucide-react";
import { toast } from "sonner";

const PendingBankingRequests = () => {
  const { t } = useTranslation();
  const [validationDialog, setValidationDialog] = useState({
    isOpen: false,
    type: null,
    user: null,
  });

  const { data: pendingRequests, isLoading, error, refetch } = useQuery({
    queryKey: ["pending-banking"],
    queryFn: () => userService.getPendingBankingRequests(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const handleApprove = (user) => {
    setValidationDialog({
      isOpen: true,
      type: "approve",
      user,
    });
  };

  const handleReject = (user) => {
    setValidationDialog({
      isOpen: true,
      type: "reject",
      user,
    });
  };

  const closeDialog = () => {
    setValidationDialog({ isOpen: false, type: null, user: null });
    refetch(); // Refresh the data after approval/rejection
  };

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <X className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                {t("messages.error")}
              </h3>
              <p className="text-red-700 mb-4">
                {error.message || t("banking.loadError")}
              </p>
              <Button onClick={() => refetch()} variant="outline">
                {t("common.retry")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t("banking.pendingRequests")}</h1>
          <p className="text-muted-foreground">
            {t("banking.pendingRequestsDescription")}
          </p>
        </div>
        <Button onClick={() => refetch()} variant="outline" size="sm">
          <Clock className="h-4 w-4 mr-2" />
          {t("common.refresh")}
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : pendingRequests?.data?.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {pendingRequests.data.map((user) => (
            <Card key={user._id} className="border-yellow-200 bg-yellow-50/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Avatar>
                      <AvatarImage src={user.profileImage} />
                      <AvatarFallback>
                        {user.fullName
                          ?.split(' ')
                          .map(n => n[0])
                          .join('')
                          .toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <CardTitle className="text-sm">{user.fullName}</CardTitle>
                      <CardDescription className="text-xs">
                        {user.email}
                      </CardDescription>
                    </div>
                  </div>
                  <BankingStatus status={user.bankingStatus} />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Shield className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">CNI:</span>
                    <span className="font-mono text-xs">
                      {user.pendingBankingInfo?.cni?.number || "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Bank:</span>
                    <span className="text-xs">
                      {user.pendingBankingInfo?.bankAccount?.bankName || "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Account:</span>
                    <span className="font-mono text-xs">
                      {user.pendingBankingInfo?.bankAccount?.bankNumber || "-"}
                    </span>
                  </div>
                </div>

                {user.bankingSubmittedAt && (
                  <div className="text-xs text-muted-foreground">
                    {t("banking.submittedOn", {
                      date: new Date(user.bankingSubmittedAt).toLocaleDateString(),
                    })}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => handleApprove(user)}
                    className="flex-1"
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    {t("banking.approve")}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleReject(user)}
                    className="flex-1"
                  >
                    <X className="h-3 w-3 mr-1" />
                    {t("banking.reject")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {t("banking.noPendingRequests")}
              </h3>
              <p className="text-muted-foreground">
                {t("banking.noPendingRequestsDescription")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Banking Validation Dialog */}
      <BankingValidationDialog
        isOpen={validationDialog.isOpen}
        onClose={closeDialog}
        user={validationDialog.user}
        type={validationDialog.type}
      />
    </div>
  );
};

export default PendingBankingRequests;